/* eslint-disable @angular-eslint/directive-class-suffix */
/* eslint-disable @angular-eslint/no-host-metadata-property */
// TODO:
// 1. Add spec file for the validator directives

import { Directive, forwardRef, Inject, Input, OnChanges, Optional, SimpleChanges } from "@angular/core";
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator, ValidatorFn, Validators } from "@angular/forms";

import { Environment, ENVIRONMENT } from "../elements";

import { ElementsValidators } from "./validators";

/**
 * A base class for validator-based directives.
 */
abstract class AbstractValidatorDirective implements Validator {
  /**
   * Initialize initial state of validator to nullValidator.
   */
  private _validator: ValidatorFn = Validators.nullValidator;
  /**
   * When the value changes in the UI, call the registered function to allow the forms API to update itself.
   */
  private _onChange!: () => void;
  /**
   * Name of an input that matches directive selector attribute (e.g. `organisationNumber` for `OrganisationNumberDirective`)
   */
  abstract inputName: string;
  /**
   * Creates an instance of a validator (specific to a directive that extends this base class)
   */
  abstract createValidator(input: unknown): ValidatorFn;
  /**
   * Performs the necessary input normalization based on a specific logic of a Directive
   */
  abstract normalizeInput(input: unknown): unknown;

  /**
   * Helper function invoked from child classes to process changes (from `ngOnChanges` hook)
   */
  handleChanges(changes: SimpleChanges): void {
    if (this.inputName in changes) {
      const input = this.normalizeInput(changes[this.inputName].currentValue);
      this._validator = this.createValidator(input);
      if (this._onChange) {
        this._onChange();
      }
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this._validator(control);
  }

  registerOnValidatorChange(fn: () => void): void {
    this._onChange = fn;
  }
}

/**
 * A directive which installs the EmailValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `emailAddress` attribute.
 *
 * ### Adding a emailAddress validator
 *
 * The following example shows how to add a organisationNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel emailAddress>
 * ```
 */
@Directive({
  selector: "[emailAddress][formControlName],[emailAddress][formControl],[emailAddress][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailAddressValidator), multi: true }],
  host: { "[attr.emailAddress]": "emailAddress ? emailAddress : null" },
})
export class EmailAddressValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the emailAddress bound to this directive.
   */
  @Input() emailAddress!: string;

  inputName = "emailAddress";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.emailAddress(this.environment.region);

  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the OrganisationNumberValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `organisationNumber` attribute.
 *
 * ### Adding a organisationNumber validator
 *
 * The following example shows how to add a organisationNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel organisationNumber>
 * ```
 */
@Directive({
  selector: "[organisationNumber][formControlName],[organisationNumber][formControl],[organisationNumber][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => OrganisationNumberValidator), multi: true }],
  host: { "[attr.organisationNumber]": "organisationNumber ? organisationNumber : null" },
})
export class OrganisationNumberValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the organisationNumber bound to this directive.
   */
  @Input() organisationNumber!: string;

  inputName = "organisationNumber";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.organisationNumber(this.environment.region);

  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the PersonalNumberValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `personalNumber` attribute.
 *
 * ### Adding a personalNumber validator
 *
 * The following example shows how to add a personalNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel personalNumber>
 * ```
 *
 * **Specifing a min and max age range**
 *
 * ```html
 * <ui-input variant="text" ngModel personalNumber minAge="18" maxAge="100">
 * ```
 */
@Directive({
  selector: "[personalNumber][formControlName],[personalNumber][formControl],[personalNumber][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PersonalNumberValidator), multi: true }],
  host: { "[attr.personalNumber]": "personalNumber ? personalNumber : null" },
})
export class PersonalNumberValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the personalNumber bound to this directive.
   */
  @Input() personalNumber!: string;
  @Input() minAge?: number;
  @Input() maxAge?: number;

  inputName = "personalNumber";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.personalNumber(this.environment.region, this.minAge, this.maxAge);

  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the PhoneNumberValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `phoneNumber` attribute.
 *
 * ### Adding a phoneNumber validator
 *
 * The following example shows how to add a phoneNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel phoneNumber>
 * ```
 */
@Directive({
  selector: "[phoneNumber][formControlName],[phoneNumber][formControl],[phoneNumber][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneNumberValidator), multi: true }],
  host: { "[attr.phoneNumber]": "phoneNumber ? phoneNumber : null" },
})
export class PhoneNumberValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the phoneNumber bound to this directive.
   */
  @Input() phoneNumber!: string;

  inputName = "phoneNumber";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.phoneNumber(this.environment.region);

  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the PremiseIdValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `premiseId` attribute.
 *
 * ### Adding a premiseId validator
 *
 * The following example shows how to add a premiseId validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel premiseId>
 * ```
 */
@Directive({
  selector: "[premiseId][formControlName],[premiseId][formControl],[premiseId][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PremiseIdValidator), multi: true }],
  host: { "[attr.premiseId]": "premiseId ? premiseId : null" },
})
export class PremiseIdValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the premiseId bound to this directive.
   */
  @Input() premiseId!: string;

  inputName = "premiseId";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.premiseId(this.environment.region);

  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the PostalCodeValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `postalCode` attribute.
 *
 * ### Adding a postalCode validator
 *
 * The following example shows how to add a postalCode validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel postalCode>
 * ```
 */
@Directive({
  selector: "[postalCode][formControlName],[postalCode][formControl],[postalCode][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PostalCodeValidator), multi: true }],
  host: { "[attr.postalCode]": "postalCode ? postalCode : null" },
})
export class PostalCodeValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the postalCode bound to this directive.
   */
  @Input() postalCode!: string;

  inputName = "postalCode";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.postalCode(this.environment.region);

  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}
/**
 * A directive which installs the Passwordfor any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `password` attribute.
 *
 * ### Adding a password validator
 *
 * The following example shows how to add a password validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel password>
 * ```
 */
@Directive({
  selector: "[password][formControlName],[password][formControl],[password][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PasswordValidator), multi: true }],
  host: { "[attr.password]": "password ? password : null" },
})
export class PasswordValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the password bound to this directive.
   */
  @Input() password!: string;

  inputName = "password";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.password();

  constructor() {
    super();
  }

  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the MinValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `min` attribute.
 *
 * ### Adding a min validator
 *
 * The following example shows how to add a min validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel min="2">
 * ```
 */
@Directive({
  selector: "[min][formControlName],[min][formControl],[min][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => MinValidator), multi: true }],
  host: { "[attr.min]": "min ? min : null" },
})
export class MinValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the min bound to this directive.
   * TODO: Add support for minimum date allowed
   */
  @Input() min!: string | number | Date;

  inputName = "min";

  normalizeInput = (input: string): number => parseInt(input, 10);

  createValidator = (min: number): ValidatorFn => Validators.min(min);
  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the MaxValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `max` attribute.
 *
 * ### Adding a max validator
 *
 * The following example shows how to add a max validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel max="10">
 * ```
 */
@Directive({
  selector: "[max][formControlName],[max][formControl],[max][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => MaxValidator), multi: true }],
  host: { "[attr.max]": "max ? max : null" },
})
export class MaxValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the max bound to this directive.
   * TODO: Add support for maximum date allowed
   */
  @Input() max!: string | number | Date;

  inputName = "max";

  normalizeInput = (input: string): number => parseInt(input, 10);

  createValidator = (max: number): ValidatorFn => Validators.max(max);
  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the GridAreaIdValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `gridAreaId` attribute.
 *
 * ### Adding a gridAreaId validator
 *
 * The following example shows how to add a gridAreaId validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel gridAreaId>
 * ```
 */
@Directive({
  selector: "[gridAreaId][formControlName],[gridAreaId][formControl],[gridAreaId][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => GridAreaIdValidator), multi: true }],
  host: { "[attr.gridAreaId]": "gridAreaId ? gridAreaId : null" },
})
export class GridAreaIdValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the gridAreaId bound to this directive.
   */
  @Input() gridAreaId!: string;

  inputName = "gridAreaId";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.gridAreaId();
  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the StreetNameValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `streetName` attribute.
 *
 * ### Adding a streetName validator
 *
 * The following example shows how to add a streetName validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel streetName>
 * ```
 */
@Directive({
  selector: "[streetName][formControlName],[streetName][formControl],[streetName][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => StreetNameValidator), multi: true }],
  host: { "[attr.streetName]": "streetName ? streetName : null" },
})
export class StreetNameValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the streetName bound to this directive.
   */
  @Input() streetName!: string;

  inputName = "streetName";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.streetName();
  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the CareOfAddressValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `careOfAddress` attribute.
 *
 * ### Adding a careOfAddress validator
 *
 * The following example shows how to add a careOfAddress validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel careOfAddress>
 * ```
 */
@Directive({
  selector: "[careOfAddress][formControlName],[careOfAddress][formControl],[careOfAddress][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => CareOfAddressValidator), multi: true }],
  host: { "[attr.careOfAddress]": "careOfAddress ? careOfAddress : null" },
})
export class CareOfAddressValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the careOfAddress bound to this directive.
   */
  @Input() careOfAddress!: string;

  inputName = "careOfAddress";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.careOfAddress();
  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}

/**
 * A directive which installs the PostalTownValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `postalTown` attribute.
 *
 * ### Adding a postalTown validator
 *
 * The following example shows how to add a postalTown validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel postalTown>
 * ```
 */
@Directive({
  selector: "[postalTown][formControlName],[postalTown][formControl],[postalTown][ngModel]",
  providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PostalTownValidator), multi: true }],
  host: { "[attr.postalTown]": "postalTown ? postalTown : null" },
})
export class PostalTownValidator extends AbstractValidatorDirective implements OnChanges {
  /**
   * Tracks changes to the careOfAddress bound to this directive.
   */
  @Input() postalTown!: string;

  inputName = "postalTown";

  normalizeInput = (input: string): string => input;

  createValidator = (): ValidatorFn => ElementsValidators.postalTown();
  /**
   * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
   * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
   * AOT mode. This could be refactored once ViewEngine is removed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.handleChanges(changes);
  }
}
